import { type Theme, type ThemeOptions } from '@mui/material/styles';

/** Kept identical between the summary and details so the title and body align on the sides. */
const ACCORDION_HORIZONTAL_PADDING = '24px';

// Mirrors IconButton's medium/contained/secondary look (see IconButton.theme.tsx) — duplicated
// rather than reused because AccordionSummary's root is already a button, so the toggle glyph
// can't be a real nested IconButton. Exported so Accordion.tsx can also apply them inline for the
// interactiveTitle branch, whose elements aren't real MuiAccordionSummary descendants and can't
// reliably rely on these class-based selectors alone (see Accordion.tsx).
export const TOGGLE_ICON_BOX_PX = 34;
export const TOGGLE_ICON_GLYPH_PX = 18;
export const TOGGLE_ICON_BG = '#F2F4F9';
export const TOGGLE_ICON_COLOR = '#555F6C';

export function accordionThemeComponents(theme: Theme): ThemeOptions['components'] {
  return {
    MuiAccordion: {
      styleOverrides: {
        root: {
          '&.SmAccordion-root': {
            backgroundColor: theme.palette.common.white,
            border: '0.5px solid',
            borderColor: theme.palette.divider,
            borderRadius: '22px',
            marginBottom: '24px',
            boxShadow: theme.shadows[1],
            '&:before': { display: 'none' },
            '&.Mui-expanded': { borderRadius: '22px', margin: 0, marginBottom: '24px' }
          },
          // Header wraps the (button-based) summary plus the optional actions slot, since a real
          // IconButton can't nest inside AccordionSummary's own button root — see Accordion.tsx.
          '& .SmAccordion-header': {
            display: 'flex',
            flexWrap: 'nowrap',
            alignItems: 'center',
            gap: '12px',
            backgroundColor: theme.palette.common.white,
            padding: ACCORDION_HORIZONTAL_PADDING,
            borderRadius: '22px'
          },
          '&.Mui-expanded .SmAccordion-header': {
            borderRadius: '22px 22px 0 0'
          },
          '& .SmAccordion-actions': {
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }
        }
      }
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          '&.SmAccordion-summary': {
            flex: 1,
            minWidth: 0,
            padding: 0,
            backgroundColor: 'transparent',
            color: theme.palette.text.primary,
            textAlign: 'right',
            minHeight: 'auto',
            '&.Mui-expanded': {
              minHeight: 'auto'
            },
            '& .MuiAccordionSummary-content': {
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              margin: 0,
              gap: ACCORDION_HORIZONTAL_PADDING
            },
            '& .MuiAccordionSummary-content.Mui-expanded': {
              margin: 0
            },
            '& .SmAccordion-titleRow': {
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: '12px',
              width: '100%'
            },
            // Rendered inset by the summary's own padding — flush with where the title text and
            // the details content start — instead of a full-bleed border across the whole width.
            '& .SmAccordion-divider': {
              width: '100%',
              borderColor: theme.palette.divider,
              borderBottomWidth: 3
            },
            '& .SmAccordion-toggleIcon': {
              width: TOGGLE_ICON_BOX_PX,
              height: TOGGLE_ICON_BOX_PX,
              flexShrink: 0,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: TOGGLE_ICON_BG,
              color: TOGGLE_ICON_COLOR,
              '& .MuiSvgIcon-root': {
                fontSize: TOGGLE_ICON_GLYPH_PX,
                width: TOGGLE_ICON_GLYPH_PX,
                height: TOGGLE_ICON_GLYPH_PX
              }
            }
          }
        }
      }
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          '&.SmAccordion-details': {
            padding: `0 ${ACCORDION_HORIZONTAL_PADDING} ${ACCORDION_HORIZONTAL_PADDING} ${ACCORDION_HORIZONTAL_PADDING}`,
            borderRadius: '0 0 22px 22px'
          }
        }
      }
    }
  };
}
